import { Component } from '@theme/component';

/** Attach media only near its viewport; an explicit play always remains possible. */
export function bindDeferredVideo(video, { autoplay = true, rootMargin = '0px', button = null } = {}) {
  const controller = new AbortController();
  const { signal } = controller;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  let visible = false;
  let nearby = false;
  let manual = false;
  let userPaused = false;
  let automaticPause = false;
  const allowsAutoplay = () => autoplay && !motion.matches && !connection?.saveData;
  const hydrate = () => {
    let changed = false;
    for (const source of video.querySelectorAll('source[data-src]')) {
      if (!source.hasAttribute('src')) {
        source.src = source.dataset.src;
        changed = true;
      }
    }
    if (changed || video.error) video.load();
  };
  const pause = () => {
    if (!video.paused) {
      automaticPause = true;
      video.pause();
    }
  };
  const sync = () => {
    if (document.visibilityState !== 'visible') return pause();
    if (nearby && (rootMargin !== '0px' || visible) && allowsAutoplay()) hydrate();
    if (!visible || (!allowsAutoplay() && !manual) || userPaused) return pause();
    hydrate();
    video.muted = true;
    video.playsInline = true;
    video.play()?.catch(() => { if (button) button.hidden = false; });
  };
  if (button) {
    button.hidden = false;
    button.addEventListener('click', () => {
      manual = true;
      userPaused = false;
      video.controls = true;
      hydrate();
      video.play()?.catch(() => { button.hidden = false; });
    }, { signal });
    video.addEventListener('playing', () => { button.hidden = true; }, { signal });
    video.addEventListener('pause', () => {
      if (!automaticPause) userPaused = true;
      automaticPause = false;
      button.hidden = false;
    }, { signal });
  }
  const observer = new IntersectionObserver((entries) => {
    visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35);
    sync();
  }, { threshold: [0, 0.35] });
  const preloadObserver = new IntersectionObserver((entries) => {
    nearby = entries.some((entry) => entry.isIntersecting);
    sync();
  }, { rootMargin });
  observer.observe(video);
  preloadObserver.observe(video);
  motion.addEventListener('change', sync, { signal });
  connection?.addEventListener?.('change', sync, { signal });
  document.addEventListener('visibilitychange', sync, { signal });
  window.addEventListener('pageshow', sync, { signal });
  const cleanup = () => {
    controller.abort();
    observer.disconnect();
    preloadObserver.disconnect();
    pause();
  };
  cleanup.refresh = sync;
  return cleanup;
}

class EchtDeferredVideo extends Component {
  #cleanup;
  connectedCallback() {
    super.connectedCallback();
    const video = this.querySelector('video');
    if (video) this.#cleanup = bindDeferredVideo(video, { rootMargin: '250px' });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup?.();
  }
}
if (!customElements.get('echt-deferred-video')) customElements.define('echt-deferred-video', EchtDeferredVideo);
